import {
  addLocalStorageItem,
  deleteLocalStorageItem,
  getLocalStorageItem,
} from "./localStorageHelper";
import getuuid from "./uuidHelper";
import {
  ApiUrls,
  LSApiTokenKey,
  LSTokenKey,
  LSRefreshTokenKey,
  LSDeviceIdKey,
  LSExpiryKey,
  UserCookie,
} from "../utils/constants";
import axios from "axios";
import moment from "moment";
import config from "../config.json";
import { getCookie } from "../utils/cookieHelper";
import { checkEmptyVal } from "../utils/common";
import { getPortalClientId } from "./clientHelper";

let tokenRequestPromise = null;
let refreshPromise = null;

const REFRESH_WINDOW_MS = 5 * 60 * 1000;

const basicAuthHeader = () =>
  "Basic " +
  btoa(`${config.apiClient.clientId}:${config.apiClient.clientSecret}`);

// APIs that build client portal links (emails, share links / QR, Stripe return URLs) from the
// portal ClientId. The ClientId of the portal the user is on is added to their payload here,
// so no call is missed. Other APIs either already send ClientId or do not need it.
const ClientIdApis = [
  ApiUrls.sendResetPassword,
  ApiUrls.createUserConnection,
  ApiUrls.createBusinessMember,
  ApiUrls.getPaymentSubAccountDetails,
  ApiUrls.getAccessibleDocuments,
  ApiUrls.getDocumentDetails,
  ApiUrls.getDocumentFolderDetails,
  ApiUrls.getDocumentsList,
  ApiUrls.getSharedDocuments,
  ApiUrls.getUserAgreementsList,
  ApiUrls.getUserAgreementDetails,
  ApiUrls.getUserAgreementFolderDetails,
  ApiUrls.getSentUserAgreementsList,
  ApiUrls.getReceivedUserAgreementsList,
  ApiUrls.getExecutedUserAgreementsList,
].map((path) => path.toLowerCase());

// Adds ClientId (0 = direct AssetsWatch) to the payload of ClientIdApis calls, unless the caller set it.
const withPortalClientId = (url, data) => {
  const path = (url || "").toLowerCase();
  if (!ClientIdApis.some((api) => path.endsWith(api))) return data;
  const clientId = getPortalClientId();
  if (data instanceof FormData) {
    if (!data.has("ClientId")) data.append("ClientId", clientId);
    return data;
  }
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data.ClientId !== undefined ? data : { ...data, ClientId: clientId };
  }
  return data;
};

const staticHeaders = () => ({
  Authorization: basicAuthHeader(),
  "X-Client-Platform": config.apiClient.platform,
});

export async function axiosGet(url, headerConfigs) {
  return withRetryOn401(async () => {
    const headers = await buildHeaders(headerConfigs);
    return axios.get(url, { headers });
  });
}

export async function axiosPost(url, data, headerConfigs) {
  return withRetryOn401(async () => {
    const headers = await buildHeaders(headerConfigs);
    return axios.post(url, withPortalClientId(url, data), { headers });
  });
}

export async function axiosPostBlob(url, data, headerConfigs) {
  return withRetryOn401(async () => {
    const headers = await buildHeaders(headerConfigs);
    return axios.post(url, withPortalClientId(url, data), { headers, responseType: "blob" });
  });
}

export async function fetchPost(url, data, headerConfigs) {
  return withRetryOn401Fetch(async () => {
    const headers = await buildHeaders(headerConfigs);
    return await fetch(url, {
      method: "POST",
      body: JSON.stringify(withPortalClientId(url, data)),
      headers,
    });
  });
}

async function buildHeaders(headerConfigs) {
  const auth = await getToken();
  return {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    ...staticHeaders(),
    ...auth,
    ...headerConfigs,
  };
}

// axios throws on non-2xx; on 401 try refresh (or full re-mint) once, then retry.
async function withRetryOn401(doCall) {
  try {
    return await doCall();
  } catch (err) {
    if (err?.response?.status !== 401) throw err;
    const recovered = await recoverAuth();
    if (!recovered) throw err;
    return await doCall();
  }
}

// fetch resolves for non-2xx; check .ok manually.
async function withRetryOn401Fetch(doCall) {
  const resp = await doCall();
  if (resp.status !== 401) return resp;
  const recovered = await recoverAuth();
  if (!recovered) return resp;
  return await doCall();
}

// Try refresh first; if that fails, wipe cache so next getToken() does a full re-mint.
async function recoverAuth() {
  const refreshed = await refreshToken();
  if (refreshed) return true;
  deleteLocalStorageItem(LSApiTokenKey);
  return true;
}

export async function getToken() {
  let tokenDetails = JSON.parse(getLocalStorageItem(LSApiTokenKey));
  const objUserCookie = getCookie(UserCookie.CookieName);
  const uaccid = checkEmptyVal(objUserCookie?.[UserCookie.AccountId])
    ? ""
    : objUserCookie?.[UserCookie.AccountId];
  const transactionId = getuuid();
  const usessid = checkEmptyVal(objUserCookie?.[UserCookie.SessionId])
    ? "web_" + transactionId
    : objUserCookie?.[UserCookie.SessionId];

  if (tokenDetails && isExpired(tokenDetails)) {
    deleteLocalStorageItem(LSApiTokenKey);
    tokenDetails = null;
  }

  if (!tokenDetails) {
    tokenDetails = await requestTokenOnce();
  } else if (isNearExpiry(tokenDetails)) {
    // Proactive refresh: 5 min before expiry we swap tokens quietly so the user
    // never sees a 401.
    const refreshed = await refreshToken();
    if (refreshed) tokenDetails = refreshed;
  }

  return {
    Token: tokenDetails?.[LSTokenKey],
    DeviceId: tokenDetails?.[LSDeviceIdKey],
    AccountId: uaccid,
    SessionId: usessid,
    TransactionId: transactionId,
  };
}

function isExpired(td) {
  if (!td?.[LSExpiryKey]) return true;
  return moment.utc().toISOString() >= new Date(td[LSExpiryKey]).toISOString();
}

function isNearExpiry(td) {
  if (!td?.[LSExpiryKey]) return true;
  return new Date(td[LSExpiryKey]).getTime() - Date.now() < REFRESH_WINDOW_MS;
}

async function requestTokenOnce() {
  if (!tokenRequestPromise) {
    tokenRequestPromise = requestToken().finally(() => {
      tokenRequestPromise = null;
    });
  }
  return await tokenRequestPromise;
}

async function requestToken() {
  const resData = {};
  const deviceId = getuuid();

  try {
    const res = await axios.post(
      `${config.apiBaseUrl}${ApiUrls.getToken}`,
      { DeviceId: deviceId },
      { headers: staticHeaders() }
    );
    if (res.data.StatusCode === 200) {
      resData[LSTokenKey] = res.data.Data.Token;
      resData[LSRefreshTokenKey] = res.data.Data.RefreshToken;
      resData[LSDeviceIdKey] = deviceId;
      resData[LSExpiryKey] = res.data.Data.Expires;
      addLocalStorageItem(LSApiTokenKey, JSON.stringify(resData));
    } else {
      console.error(res);
    }
  } catch (err) {
    console.error(err);
  }

  return resData;
}

// Exchange the refresh token for a fresh pair. Returns new tokenDetails on
// success, null on failure (caller falls back to full re-mint).
async function refreshToken() {
  if (refreshPromise) return await refreshPromise;

  refreshPromise = (async () => {
    const cached = JSON.parse(getLocalStorageItem(LSApiTokenKey));
    if (!cached?.[LSRefreshTokenKey] || !cached?.[LSDeviceIdKey]) return null;

    try {
      const res = await axios.post(
        `${config.apiBaseUrl}${ApiUrls.refreshToken}`,
        { RefreshToken: cached[LSRefreshTokenKey] },
        {
          headers: {
            "Content-Type": "application/json",
            ...staticHeaders(),
            DeviceId: cached[LSDeviceIdKey],
            TransactionId: getuuid(),
          },
        }
      );
      if (res.data.StatusCode === 200) {
        const next = {
          [LSTokenKey]: res.data.Data.Token,
          [LSRefreshTokenKey]: res.data.Data.RefreshToken,
          [LSDeviceIdKey]: cached[LSDeviceIdKey],
          [LSExpiryKey]: res.data.Data.Expires,
        };
        addLocalStorageItem(LSApiTokenKey, JSON.stringify(next));
        return next;
      }
      return null;
    } catch (err) {
      console.error(err);
      return null;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return await refreshPromise;
}
